import { create } from 'zustand';
import { User, Driver, Client, Depot, Dumpster, Task, UserRole, PhytoProduct, EngraisProduct, NewTask, FuelTask, DumpsterTask, PhytoTask, EngraisTask } from '../types';
import { supabase } from '../lib/supabase';
import { updateDriverPosition } from '../utils/network';
import { setRememberMePreference } from '../lib/authStorage';

const DEPOT_POSITION = { lat: 44.260571, lng: 0.960050 };

// ID de la benne "inconnue" - à remplacer par l'ID réel de votre benne "Benne Inconnue"
const UNKNOWN_DUMPSTER_ID = 'unknown-dumpster-id';

interface AppState {
  currentUser: User | null;
  isAuthenticated: boolean;
  users: User[];
  drivers: Driver[];
  clients: Client[];
  depots: Depot[];
  dumpsters: Dumpster[];
  tasks: Task[];
  
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  loadInitialData: () => Promise<void>;
  addTask: (task: NewTask) => Promise<Task>;
  updateTask: (taskId: string, taskData: Partial<Task>) => Promise<void>;
  completeTask: (taskId: string, data: any) => Promise<void>;
  cancelTask: (taskId: string) => Promise<void>;
  deleteTask: (taskId: string, password: string) => Promise<void>;
  startTask: (taskId: string) => Promise<void>;
  addClient: (client: { firstName: string; lastName: string; companyName: string; streetAddress: string; postalCode: string; city: string; email?: string; phone?: string; position: { lat: number; lng: number } }) => Promise<void>;
  updateClient: (clientId: string, clientData: { firstName: string; lastName: string; companyName: string; streetAddress: string; postalCode: string; city: string; email?: string; phone?: string }) => Promise<void>;
  updateUser: (userId: string, userData: { firstName: string; lastName: string; email: string; role: UserRole }) => Promise<void>;
  addUser: (userData: { firstName: string; lastName: string; email: string; password: string; role: UserRole }) => Promise<void>;
  resetDriverPassword: (userId: string) => Promise<void>;
  setUserPassword: (userId: string, newPassword: string) => Promise<void>;
  addDepot: (depot: { id: string; name: string; position: { lat: number; lng: number } }) => Promise<void>;
  addDumpster: (dumpster: { name: string; number: string; comment?: string }) => Promise<void>;
  updateDumpster: (dumpsterId: string, dumpsterData: Partial<Dumpster>) => Promise<void>;
  deleteDumpster: (dumpsterId: string) => Promise<void>;
  assignDumpsterToTask: (taskId: string, dumpsterId: string) => Promise<void>;
  toggleDumpsterActiveStatus: (dumpsterId: string, isActive: boolean) => Promise<void>;
}

const useAppStore = create<AppState>((set, get) => ({
  currentUser: null,
  isAuthenticated: false,
  users: [],
  drivers: [],
  clients: [],
  depots: [],
  dumpsters: [],
  tasks: [],

  addClient: async (client) => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .insert({
          first_name: client.firstName,
          last_name: client.lastName,
          company_name: client.companyName,
          street_address: client.streetAddress,
          postal_code: client.postalCode,
          city: client.city,
          email: client.email || null,
          phone: client.phone || null,
          position_lat: client.position.lat,
          position_lng: client.position.lng,
          created_by: get().currentUser?.id
        })
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        clients: [...state.clients, {
          id: data.id,
          firstName: data.first_name,
          lastName: data.last_name,
          companyName: data.company_name,
          streetAddress: data.street_address,
          postalCode: data.postal_code,
          city: data.city,
          email: data.email,
          phone: data.phone,
          position: {
            lat: data.position_lat,
            lng: data.position_lng,
          },
        }]
      }));
    } catch (error) {
      console.error('Error adding client:', error);
      throw error;
    }
  },

  updateClient: async (clientId, clientData) => {
    try {
      const { error } = await supabase
        .from('clients')
        .update({
          first_name: clientData.firstName,
          last_name: clientData.lastName,
          company_name: clientData.companyName,
          street_address: clientData.streetAddress,
          postal_code: clientData.postalCode,
          city: clientData.city,
          email: clientData.email || null,
          phone: clientData.phone || null
        })
        .eq('id', clientId);

      if (error) throw error;

      set(state => ({
        clients: state.clients.map(client =>
          client.id === clientId
            ? {
                ...client,
                firstName: clientData.firstName,
                lastName: clientData.lastName,
                companyName: clientData.companyName,
                streetAddress: clientData.streetAddress,
                postalCode: clientData.postalCode,
                city: clientData.city,
                email: clientData.email,
                phone: clientData.phone
              }
            : client
        )
      }));
    } catch (error) {
      console.error('Error updating client:', error);
      throw error;
    }
  },

  updateUser: async (userId, userData) => {
    try {
      // Only update the public users table
      const { error: dbError } = await supabase
        .from('users')
        .update({
          first_name: userData.firstName,
          last_name: userData.lastName,
          email: userData.email,
          role: userData.role
        })
        .eq('id', userId);

      if (dbError) throw dbError;

      // Let the database trigger handle auth.users synchronization

      if (userData.role === 'driver') {
        const { data: driverData } = await supabase
          .from('drivers')
          .select('*')
          .eq('id', userId)
          .single();
          
        if (!driverData) {
          const { error: driverError } = await supabase
            .from('drivers')
            .insert({
              id: userId,
              is_active: false,
              position_lat: DEPOT_POSITION.lat,
              position_lng: DEPOT_POSITION.lng
            });
            
          if (driverError) throw driverError;
        }
      }

      set(state => ({
        users: state.users.map(user =>
          user.id === userId
            ? {
                ...user,
                firstName: userData.firstName,
                lastName: userData.lastName,
                email: userData.email,
                role: userData.role
              }
            : user
        ),
        currentUser: state.currentUser?.id === userId
          ? {
              ...state.currentUser,
              firstName: userData.firstName,
              lastName: userData.lastName,
              email: userData.email,
              role: userData.role
            }
          : state.currentUser
      }));
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  addUser: async (userData) => {
    try {
      // First, check if the user is already logged in somewhere else
      const { data: existingUser, error: checkError } = await supabase
        .from('users')
        .select('*')
        .eq('email', userData.email)
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      const { data: authData, error: signUpError } = await supabase.auth.admin.createUser({
        email: userData.email,
        password: userData.password,
        email_confirm: true,
        user_metadata: {
          first_name: userData.firstName,
          last_name: userData.lastName,
          role: userData.role
        }
      });

      if (signUpError) throw signUpError;

      if (!authData.user) {
        throw new Error("Failed to create user");
      }

      const { error: userError } = await supabase
        .from('users')
        .insert({
          id: authData.user.id,
          email: userData.email,
          first_name: userData.firstName,
          last_name: userData.lastName,
          role: userData.role,
          is_online: false
        });

      if (userError) throw userError;

      if (userData.role === 'driver') {
        const { error: driverError } = await supabase
          .from('drivers')
          .insert({
            id: authData.user.id,
            is_active: false,
            position_lat: DEPOT_POSITION.lat,
            position_lng: DEPOT_POSITION.lng
          });
          
        if (driverError) throw driverError;
      }

      set(state => ({
        users: [...state.users, {
          id: authData.user.id,
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          role: userData.role,
          isOnline: false
        }]
      }));
    } catch (error) {
      console.error('Error adding user:', error);
      throw error;
    }
  },

  resetDriverPassword: async (userId) => {
    try {
      const userEmail = get().users.find(u => u.id === userId)?.email;
      
      if (!userEmail) {
        throw new Error('User email not found');
      }
      
      // Utiliser l'URL publique de l'application au lieu de window.location.origin
      const publicAppUrl = import.meta.env.VITE_PUBLIC_APP_URL || 'https://web.appligerbe.fr';
      
      const { error } = await supabase.auth.resetPasswordForEmail(userEmail, {
        redirectTo: `${publicAppUrl}/login`
      });
      
      if (error) throw error;
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  },

  setUserPassword: async (userId, newPassword) => {
    try {
      // Vérifier que l'utilisateur est un administrateur
      if (get().currentUser?.role !== 'admin') {
        throw new Error('Seuls les administrateurs peuvent changer les mots de passe');
      }

      // Vérifier que le mot de passe est suffisamment fort
      if (newPassword.length < 6) {
        throw new Error('Le mot de passe doit contenir au moins 6 caractères');
      }

      // Obtenir la session actuelle pour l'authentification
      const { data: sessionData } = await supabase.auth.getSession();
      
      if (!sessionData.session) {
        throw new Error('Aucune session active trouvée');
      }

      // Appeler la fonction Edge pour changer le mot de passe
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/set-user-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.session.access_token}`
        },
        body: JSON.stringify({
          userId,
          newPassword
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la modification du mot de passe');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error setting user password:', error);
      throw error;
    }
  },

  login: async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      // Save the rememberMe preference before login
      setRememberMePreference(rememberMe);

      // Sign out any existing session
      await supabase.auth.signOut();

      await new Promise(resolve => setTimeout(resolve, 100));

      const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) throw signInError;

      // Now check if user exists in users table
      const { data: userDataFull, error: userFullError } = await supabase
        .from('users')
        .select('*')
        .eq('id', authData.user.id)
        .single();

      if (userFullError) throw userFullError;

      // Update user status to online
      const { error: updateError } = await supabase
        .from('users')
        .update({ is_online: true, last_active: new Date().toISOString() })
        .eq('id', authData.user.id);

      if (updateError) throw updateError;

      set({
        currentUser: {
          id: userDataFull.id,
          email: userDataFull.email,
          firstName: userDataFull.first_name,
          lastName: userDataFull.last_name,
          role: userDataFull.role as UserRole,
          isOnline: true,
          lastActive: userDataFull.last_active
        },
        isAuthenticated: true,
      });

      await get().loadInitialData();
    } catch (error) {
      console.error('Login error:', error);
      await supabase.auth.signOut();
      localStorage.clear();
      set({
        currentUser: null,
        isAuthenticated: false,
        users: [],
        drivers: [],
        clients: [],
        depots: [],
        dumpsters: [],
        tasks: [],
      });
      throw error;
    }
  },

  logout: async () => {
    try {
      // Mark user as offline before signing out
      const currentUser = get().currentUser;
      if (currentUser?.id) {
        await supabase
          .from('users')
          .update({ is_online: false })
          .eq('id', currentUser.id);
      }

      set({
        currentUser: null,
        isAuthenticated: false,
        users: [],
        drivers: [],
        clients: [],
        depots: [],
        dumpsters: [],
        tasks: [],
      });

      localStorage.clear();

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

    } catch (error) {
      console.error('Error during logout:', error);
      localStorage.clear();
      throw error;
    }
  },

  loadInitialData: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        set({
          currentUser: null,
          isAuthenticated: false,
          users: [],
          drivers: [],
          clients: [],
          depots: [],
          dumpsters: [],
          tasks: [],
        });
        return;
      }
      
      const [
        { data: users },
        { data: drivers },
        { data: clients },
        { data: depots },
        { data: dumpsters },
        { data: tasks },
      ] = await Promise.all([
        supabase.from('users').select('*'),
        supabase.from('drivers').select('*'),
        supabase.from('clients').select('*'),
        supabase.from('depots').select('*'),
        supabase.from('dumpsters').select('*'),
        // Order tasks by created_at in descending order to get the most recent first
        supabase.from('tasks').select('*').order('created_at', { ascending: false }),
      ]);

      const currentUserId = session.user.id;
      const currentUserData = users?.find(u => u.id === currentUserId);
      
      if (!currentUserData) {
        await supabase.auth.signOut();
        set({
          currentUser: null,
          isAuthenticated: false,
          users: [],
          drivers: [],
          clients: [],
          depots: [],
          dumpsters: [],
          tasks: [],
        });
        return;
      }

      // Update user's last_active timestamp
      await supabase
        .from('users')
        .update({ last_active: new Date().toISOString() })
        .eq('id', currentUserId);

      set({
        currentUser: {
          id: currentUserData.id,
          firstName: currentUserData.first_name,
          lastName: currentUserData.last_name,
          email: currentUserData.email,
          role: currentUserData.role,
          isOnline: currentUserData.is_online,
          lastActive: currentUserData.last_active
        },
        isAuthenticated: true,
        users: users?.map(u => ({
          id: u.id,
          firstName: u.first_name,
          lastName: u.last_name,
          email: u.email,
          role: u.role,
          isOnline: u.is_online,
          lastActive: u.last_active
        })) || [],
        drivers: drivers?.map(d => {
          const user = users?.find(u => u.id === d.id);
          if (!user) return null;

          const driver: Driver = {
            id: d.id,
            firstName: user.first_name,
            lastName: user.last_name,
            email: user.email,
            role: 'driver' as const,
            position: d.position_lat !== null && d.position_lng !== null ? {
              lat: d.position_lat,
              lng: d.position_lng,
            } : undefined,
            isActive: d.is_active,
            activeTasks: [],
            power: d.power || false,
            isOnline: user.is_online,
            lastActive: user.last_active,
          };
          return driver;
        }).filter((d): d is Driver => d !== null) || [],
        clients: clients?.map(c => ({
          id: c.id,
          firstName: c.first_name,
          lastName: c.last_name,
          companyName: c.company_name,
          streetAddress: c.street_address,
          postalCode: c.postal_code,
          city: c.city,
          email: c.email,
          phone: c.phone,
          position: {
            lat: c.position_lat,
            lng: c.position_lng,
          },
        })) || [],
        depots: depots?.map(d => ({
          id: d.id,
          name: d.name,
          position: {
            lat: d.position_lat,
            lng: d.position_lng,
          },
        })) || [],
        dumpsters: dumpsters?.map(d => {
          // Find the pose task for this dumpster to get the client name
          const poseTask = tasks?.find(t => 
            t.type === 'dumpster' && 
            t.dumpster_id === d.id && 
            t.dumpster_task_type === 'pose'
          );
          
          let placedByClientName;
          if (poseTask) {
            const client = clients?.find(c => c.id === poseTask.client_id);
            placedByClientName = client?.company_name;
          }
          
          // Find the latest completed pose task for this dumpster
          const completedPoseTask = tasks?.filter(t =>
            t.type === 'dumpster' &&
            t.dumpster_id === d.id &&
            t.dumpster_task_type === 'pose' &&
            t.status === 'completed'
          ).sort((a, b) => {
            const dateA = new Date(a.end_time || a.date);
            const dateB = new Date(b.end_time || b.date);
            return dateB.getTime() - dateA.getTime();
          })[0];

          const dropDate = completedPoseTask?.end_time || d.drop_date;
          const driverId = completedPoseTask?.driver_id || d.driver_id;

          // Position comes exclusively from the last completed pose task's drop_location coordinates
          const position = completedPoseTask?.drop_location_lat && completedPoseTask?.drop_location_lng
            ? { lat: completedPoseTask.drop_location_lat, lng: completedPoseTask.drop_location_lng }
            : undefined;

          return {
            id: d.id,
            name: d.name,
            number: d.number,
            comment: d.comment,
            position,
            clientId: d.client_id,
            depotId: d.depot_id,
            dropDate: dropDate,
            driverId: driverId,
            placedByClientName,
            isActive: d.is_active !== false,
          };
        }) || [],
        tasks: tasks?.map(t => {
          const baseTask = {
            id: t.id,
            driverId: t.driver_id,
            clientId: t.client_id,
            locationId: t.location_id,
            date: t.date,
            status: t.status,
            startTime: t.start_time,
            endTime: t.end_time,
            createdBy: t.created_by,
            updatedAt: t.updated_at,
            cancelledAt: t.cancelled_at,
            cancelledBy: t.cancelled_by,
            finishedBy: t.finished_by,
            comment: t.comment,
          };

          if (t.type === 'fuel') {
            return {
              ...baseTask,
              type: 'fuel' as const,
              fuelType: t.fuel_type!,
              plannedLiters: t.planned_liters!,
              actualLiters: t.actual_liters,
              price: t.price!,
            };
          }

          if (t.type === 'dumpster') {
            return {
              ...baseTask,
              type: 'dumpster' as const,
              dumpsterId: t.dumpster_id!,
              dumpsterTaskType: t.dumpster_task_type!,
              actualQuantity: t.actual_quantity,
              dropLocation: t.drop_location_type ? {
                type: t.drop_location_type,
                id: t.drop_location_id!,
                position: {
                  lat: t.drop_location_lat!,
                  lng: t.drop_location_lng!,
                },
              } : undefined,
            };
          }

          if (t.type === 'phyto') {
            return {
              ...baseTask,
              type: 'phyto' as const,
              photos: t.photos || undefined,
              signature: t.signature || undefined,
            };
          }

          return {
            ...baseTask,
            type: 'engrais' as const,
            photos: t.photos || undefined,
            signature: t.signature || undefined,
          };
        }) || [],
      });
    } catch (error) {
      console.error('Error loading initial data:', error);
      set({
        currentUser: null,
        isAuthenticated: false,
        users: [],
        drivers: [],
        clients: [],
        depots: [],
        dumpsters: [],
        tasks: [],
      });
      throw error;
    }
  },

  addTask: async (task: NewTask) => {
    try {
      // Ajout de logs de débogage pour le commentaire
      console.log('Task object received:', task);
      console.log('Comment value:', task.comment);
      
      const taskData: any = {
        driver_id: task.driverId,
        client_id: task.clientId,
        location_id: task.locationId,
        date: task.date,
        status: task.status || 'pending',
        type: task.type,
        created_by: get().currentUser?.id,
        comment: task.comment
      };
      
      console.log('Task data being sent to Supabase:', taskData);

      if (task.type === 'fuel') {
        taskData.fuel_type = task.fuelType;
        taskData.planned_liters = task.plannedLiters;
        taskData.price = task.price;
      } else if (task.type === 'dumpster') {
        taskData.dumpster_id = task.dumpsterId || UNKNOWN_DUMPSTER_ID;
        taskData.dumpster_task_type = task.dumpsterTaskType;
      } else if (task.type === 'phyto') {
        // Phyto products will be handled separately
      } else if (task.type === 'engrais') {
        // Engrais products will be handled separately
      }

      const { data: taskResult, error: taskError } = await supabase
        .from('tasks')
        .insert(taskData)
        .select()
        .single();

      if (taskError) throw taskError;
      
      console.log('Task created in Supabase:', taskResult);
      console.log('Comment saved in database:', taskResult.comment);

      if (task.type === 'phyto' && task.products && task.products.length > 0) {
        const productsData = task.products.map(product => ({
          task_id: taskResult.id,
          name: product.name,
          planned_quantity: product.plannedQuantity
        }));

        const { error: productsError } = await supabase
          .from('phyto_products')
          .insert(productsData);

        if (productsError) throw productsError;
      }

      if (task.type === 'engrais' && task.products && task.products.length > 0) {
        const productsData = task.products.map(product => ({
          task_id: taskResult.id,
          name: product.name,
          planned_quantity_kg: product.plannedQuantityKg
        }));

        const { error: productsError } = await supabase
          .from('engrais_products')
          .insert(productsData);

        if (productsError) throw productsError;
      }

      const newTask: Task = {
        id: taskResult.id,
        driverId: taskResult.driver_id,
        clientId: taskResult.client_id,
        locationId: taskResult.location_id,
        date: taskResult.date,
        status: taskResult.status,
        createdBy: taskResult.created_by,
        updatedAt: taskResult.updated_at,
        comment: taskResult.comment,
        ...(taskResult.type === 'fuel' ? {
          type: 'fuel' as const,
          fuelType: taskResult.fuel_type,
          plannedLiters: taskResult.planned_liters,
          actualLiters: taskResult.actual_liters,
          price: taskResult.price,
        } : taskResult.type === 'dumpster' ? {
          type: 'dumpster' as const,
          dumpsterId: taskResult.dumpster_id,
          dumpsterTaskType: taskResult.dumpster_task_type,
          actualQuantity: taskResult.actual_quantity,
          dropLocation: taskResult.drop_location_type ? {
            type: taskResult.drop_location_type,
            id: taskResult.drop_location_id,
            position: {
              lat: taskResult.drop_location_lat,
              lng: taskResult.drop_location_lng,
            },
          } : undefined,
        } : taskResult.type === 'phyto' ? {
          type: 'phyto' as const,
          products: task.type === 'phyto' ? task.products : undefined,
        } : {
          type: 'engrais' as const,
          products: task.type === 'engrais' ? task.products : undefined,
        }),
      };

      set(state => ({
        tasks: [...state.tasks, newTask],
      }));

      return newTask;
    } catch (error) {
      console.error('Error adding task:', error);
      throw error;
    }
  },

  updateTask: async (taskId: string, taskData: Partial<Task>) => {
    try {
      // Convert task data to database format
      const dbTaskData: any = {};

      if (taskData.status !== undefined) dbTaskData.status = taskData.status;
      if (taskData.startTime !== undefined) dbTaskData.start_time = taskData.startTime;
      if (taskData.endTime !== undefined) dbTaskData.end_time = taskData.endTime;
      if ('actualLiters' in taskData) dbTaskData.actual_liters = (taskData as Partial<FuelTask>).actualLiters;
      if ('actualQuantity' in taskData) dbTaskData.actual_quantity = (taskData as Partial<DumpsterTask>).actualQuantity;
      if (taskData.comment !== undefined) dbTaskData.comment = taskData.comment;
      if (taskData.date !== undefined) dbTaskData.date = taskData.date;
      if ('plannedLiters' in taskData) dbTaskData.planned_liters = (taskData as Partial<FuelTask>).plannedLiters;
      if ('price' in taskData) dbTaskData.price = (taskData as Partial<FuelTask>).price;

      const { error } = await supabase
        .from('tasks')
        .update(dbTaskData)
        .eq('id', taskId);

      if (error) throw error;

      set(state => ({
        tasks: state.tasks.map(task =>
          task.id === taskId ? { ...task, ...taskData } as Task : task
        ),
      }));
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  },

  completeTask: async (taskId: string, data: any) => {
    try {
      const task = get().tasks.find(t => t.id === taskId);
      if (!task) throw new Error('Task not found');

      const updateData: any = {
        status: 'completed',
        end_time: new Date().toISOString(),
        finished_by: get().currentUser?.id,
      };

      // Add comment if provided
      if (data.comment !== undefined) {
        updateData.comment = data.comment;
      }

      if (task.type === 'fuel') {
        updateData.actual_liters = data.actualLiters;
      } else if (task.type === 'dumpster') {
        if (data.actualQuantity) {
          updateData.actual_quantity = data.actualQuantity;
        }
        
        if (data.dropLocation) {
          updateData.drop_location_type = data.dropLocation.type;
          updateData.drop_location_id = data.dropLocation.id;
          updateData.drop_location_lat = data.dropLocation.position.lat;
          updateData.drop_location_lng = data.dropLocation.position.lng;

          // For client drop locations, we need to get the actual client_id from the client_locations table
          let clientIdForDumpster = null;
          let depotIdForDumpster = null;
          
          if (data.dropLocation.type === 'client') {
            // Fetch the client_id from the client_locations table using the location_id
            const { data: locationData, error: locationError } = await supabase
              .from('client_locations')
              .select('client_id')
              .eq('id', data.dropLocation.id)
              .single();
              
            if (locationError) throw locationError;
            clientIdForDumpster = locationData.client_id;
          } else if (data.dropLocation.type === 'depot') {
            depotIdForDumpster = data.dropLocation.id;
          }

          const { error: dumpsterError } = await supabase
            .from('dumpsters')
            .update({
              position_lat: data.dropLocation.position.lat,
              position_lng: data.dropLocation.position.lng,
              client_id: clientIdForDumpster,
              depot_id: depotIdForDumpster,
              drop_date: new Date().toISOString(),
              driver_id: task.driverId
            })
            .eq('id', task.dumpsterId);

          if (dumpsterError) throw dumpsterError;

          set(state => ({
            dumpsters: state.dumpsters.map(d =>
              d.id === task.dumpsterId
                ? {
                    ...d,
                    position: {
                      lat: data.dropLocation.position.lat,
                      lng: data.dropLocation.position.lng
                    },
                    clientId: clientIdForDumpster,
                    depotId: depotIdForDumpster,
                    dropDate: new Date().toISOString(),
                    driverId: task.driverId
                  }
                : d
            )
          }));
        }
      } else if (task.type === 'phyto' && data.products) {
        for (const product of data.products) {
          const { error: productError } = await supabase
            .from('phyto_products')
            .update({ actual_quantity: product.actualQuantity })
            .eq('task_id', taskId)
            .eq('name', product.name);

          if (productError) throw productError;
        }
      } else if (task.type === 'engrais' && data.products) {
        for (const product of data.products) {
          const { error: productError } = await supabase
            .from('engrais_products')
            .update({ actual_quantity_kg: product.actualQuantityKg })
            .eq('task_id', taskId)
            .eq('name', product.name);

          if (productError) throw productError;
        }
      }

      const { error } = await supabase
        .from('tasks')
        .update(updateData)
        .eq('id', taskId);

      if (error) throw error;

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (supabaseUrl) {
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          const token = sessionData?.session?.access_token ?? import.meta.env.VITE_SUPABASE_ANON_KEY;
          await fetch(`${supabaseUrl}/functions/v1/send-completion-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ taskId }),
          });
        } catch {
          // email failure must not affect task completion
        }
      }

      set(state => ({
        tasks: state.tasks.map(t =>
          t.id === taskId
            ? {
                ...t,
                status: 'completed',
                endTime: new Date().toISOString(),
                finishedBy: get().currentUser?.id,
                comment: data.comment !== undefined ? data.comment : t.comment,
                ...(t.type === 'fuel'
                  ? { actualLiters: data.actualLiters }
                  : t.type === 'dumpster'
                  ? { 
                      dropLocation: data.dropLocation,
                      actualQuantity: data.actualQuantity
                    }
                  : { actualQuantity: data.actualQuantity }),
              }
            : t
        ),
      }));
    } catch (error) {
      console.error('Error completing task:', error);
      throw error;
    }
  },

  cancelTask: async (taskId: string) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancelled_by: get().currentUser?.id,
        })
        .eq('id', taskId);

      if (error) throw error;

      set(state => ({
        tasks: state.tasks.map(task =>
          task.id === taskId
            ? {
                ...task,
                status: 'cancelled',
                cancelledAt: new Date().toISOString(),
                cancelledBy: get().currentUser?.id,
              }
            : task
        ),
      }));
    } catch (error) {
      console.error('Error cancelling task:', error);
      throw error;
    }
  },

  deleteTask: async (taskId: string, password: string) => {
    try {
      // Verify user is admin
      if (get().currentUser?.role !== 'admin') {
        throw new Error('Only administrators can delete tasks');
      }

      // Get the session for authentication
      const { data: sessionData } = await supabase.auth.getSession();
      
      if (!sessionData.session) {
        throw new Error('No active session found');
      }

      // Call the Edge Function to delete the task
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/delete-task`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.session.access_token}`
        },
        body: JSON.stringify({
          taskId,
          password
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error deleting task');
      }

      // Remove the task from the local state
      set(state => ({
        tasks: state.tasks.filter(task => task.id !== taskId)
      }));

      return await response.json();
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  },

  startTask: async (taskId: string) => {
    try {
      const task = get().tasks.find(t => t.id === taskId);
      if (!task) throw new Error('Task not found');

      if (task.status === 'editing') {
        throw new Error('Cette tâche est en cours de modification et ne peut pas être démarrée');
      }

      const { error } = await supabase
        .from('tasks')
        .update({
          status: 'in-progress',
          start_time: new Date().toISOString(),
        })
        .eq('id', taskId)
        .eq('status', 'pending');

      if (error) throw error;

      set(state => ({
        tasks: state.tasks.map(task =>
          task.id === taskId
            ? { ...task, status: 'in-progress', startTime: new Date().toISOString() }
            : task
        ),
      }));
    } catch (error) {
      console.error('Error starting task:', error);
      throw error;
    }
  },

  addDepot: async (depot) => {
    try {
      const { data, error } = await supabase
        .from('depots')
        .insert({
          name: depot.name,
          position_lat: depot.position.lat,
          position_lng: depot.position.lng,
          created_by: get().currentUser?.id
        })
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        depots: [...state.depots, {
          id: data.id,
          name: data.name,
          position: {
            lat: data.position_lat,
            lng: data.position_lng,
          },
        }]
      }));
    } catch (error) {
      console.error('Error adding depot:', error);
      throw error;
    }
  },

  addDumpster: async (dumpster) => {
    try {
      if (!get().currentUser?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('dumpsters')
        .insert({
          name: dumpster.name,
          number: dumpster.number,
          comment: dumpster.comment || null,
          created_by: get().currentUser?.id
        })
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        dumpsters: [...state.dumpsters, {
          id: data.id,
          name: data.name,
          number: data.number,
          comment: data.comment,
          position: undefined,
          clientId: undefined,
          depotId: undefined,
          dropDate: undefined,
          driverId: undefined,
          isActive: true,
        }]
      }));
    } catch (error) {
      console.error('Error adding dumpster:', error);
      throw error;
    }
  },

  updateDumpster: async (dumpsterId, dumpsterData) => {
    try {
      const updateData: any = {};
      
      if (dumpsterData.name !== undefined) updateData.name = dumpsterData.name;
      if (dumpsterData.number !== undefined) updateData.number = dumpsterData.number;
      if (dumpsterData.comment !== undefined) updateData.comment = dumpsterData.comment;
      if (dumpsterData.position) {
        updateData.position_lat = dumpsterData.position.lat;
        updateData.position_lng = dumpsterData.position.lng;
      }
      if (dumpsterData.clientId !== undefined) updateData.client_id = dumpsterData.clientId;
      if (dumpsterData.depotId !== undefined) updateData.depot_id = dumpsterData.depotId;
      if (dumpsterData.dropDate !== undefined) updateData.drop_date = dumpsterData.dropDate;
      if (dumpsterData.driverId !== undefined) updateData.driver_id = dumpsterData.driverId;

      const { error } = await supabase
        .from('dumpsters')
        .update(updateData)
        .eq('id', dumpsterId);

      if (error) throw error;

      set(state => ({
        dumpsters: state.dumpsters.map(d =>
          d.id === dumpsterId ? { ...d, ...dumpsterData } : d
        )
      }));
    } catch (error) {
      console.error('Error updating dumpster:', error);
      throw error;
    }
  },

  deleteDumpster: async (dumpsterId) => {
    try {
      // Check if dumpster is used in any tasks
      const { data: tasksData, error: tasksError } = await supabase
        .from('tasks')
        .select('id')
        .eq('dumpster_id', dumpsterId)
        .limit(1);

      if (tasksError) throw tasksError;

      if (tasksData && tasksData.length > 0) {
        throw new Error('Cette benne est utilisée dans des tâches et ne peut pas être supprimée');
      }

      const { error } = await supabase
        .from('dumpsters')
        .delete()
        .eq('id', dumpsterId);

      if (error) throw error;

      set(state => ({
        dumpsters: state.dumpsters.filter(d => d.id !== dumpsterId)
      }));
    } catch (error) {
      console.error('Error deleting dumpster:', error);
      throw error;
    }
  },

  assignDumpsterToTask: async (taskId: string, dumpsterId: string) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ dumpster_id: dumpsterId })
        .eq('id', taskId);

      if (error) throw error;

      set(state => ({
        tasks: state.tasks.map(task =>
          task.id === taskId && task.type === 'dumpster'
            ? { ...task, dumpsterId }
            : task
        ),
      }));
    } catch (error) {
      console.error('Error assigning dumpster to task:', error);
      throw error;
    }
  },

  toggleDumpsterActiveStatus: async (dumpsterId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('dumpsters')
        .update({ is_active: isActive })
        .eq('id', dumpsterId);

      if (error) throw error;

      set(state => ({
        dumpsters: state.dumpsters.map(d =>
          d.id === dumpsterId ? { ...d, isActive } : d
        )
      }));
    } catch (error) {
      console.error('Error toggling dumpster status:', error);
      throw error;
    }
  }
}));
export default useAppStore;